import { Suspense, type FC } from 'react';

import { useTranslation } from 'react-i18next';
import { Await } from 'react-router';

import { Logger, LogLevel } from '@repo/utils/common';

import { getLocaleFromParams } from '@/shared/utils/i18n.utils';
import type { Route } from '@/router-types/src/pages/+types/main';

import { getMachineList } from '@/client/services/machine.service';

import { Container } from '@/components/common/Container';
import { MainHydrateFallback } from '@/components/fallback/MainHydrateFallback';
import { HomeHero } from '@/components/hero/HomeHero';
import { MachineGroupGrid } from '@/components/machine/MachineGroupGrid';

export const HydrateFallback = MainHydrateFallback;

const logger = new Logger(LogLevel.DEBUG);

export const clientLoader = async ({ params }: Route.ClientLoaderArgs) => {
  try {
    const locale = getLocaleFromParams(params);
    const dataPromise = getMachineList(locale);
    return { dataPromise };
  } catch (error) {
    logger.error('error:', error);
    throw new Response('', { status: 500, statusText: 'There was an error fetching the data' });
  }
};

const HomePage: FC<Route.ComponentProps> = ({ loaderData }) => {
  const { dataPromise } = loaderData || {};
  const { t } = useTranslation();

  return (
    <div className="mb-8 space-y-4">
      <HomeHero scrollTo="configuration" />
      <Container className="flex-1 py-8">
        <h2 id="configuration" className="mb-10 scroll-mt-20 text-center text-2xl font-bold">
          {t('home.pageTitle')}
        </h2>
        <Suspense fallback={<p className="text-center">{t('common.loading')}</p>}>
          <Await resolve={dataPromise}>
            {(data) => <MachineGroupGrid groups={data.machineGroups} />}
          </Await>
        </Suspense>
      </Container>
    </div>
  );
};

export default HomePage;
